import {
  AlertOutlined,
  BankOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  UsergroupAddOutlined,
  UserOutlined,
  CalendarOutlined,
  RiseOutlined,
  FallOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Card,
  Col,
  Row,
  Space,
  Statistic,
  Table,
  Typography,
  Progress,
  Tag,
  Tooltip,
  Spin,
  Alert,
  Empty,
  Button,
} from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AccumulationChartComponent,
  AccumulationDataLabel,
  AccumulationLegend,
  AccumulationSeriesCollectionDirective,
  AccumulationSeriesDirective,
  AccumulationTooltip,
  Inject,
  PieSeries,
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  Category,
  ColumnSeries,
  Legend,
  DataLabel,
  LineSeries,
} from "@syncfusion/ej2-react-charts";
import axios from "axios";
import { BASE_URL } from "../../Constants";

const SuperAdminDashboard = () => {
  const { t } = useTranslation();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { Text, Title } = Typography;
  
  const legendSettings = {
    visible: true,
    position: "Bottom",
    textStyle: { size: "12px" },
  };

  // Transform employee data for pie chart
  const employeeDistribution = dashboardData ? [
    {
      category: t("dashboard.activeEmployees"),
      amount: dashboardData.activeUsers || 0,
      color: "#1890ff"
    },
    {
      category: t("dashboard.inactiveEmployees"), 
      amount: dashboardData.inactiveUsers || 0,
      color: "#d9d9d9"
    },
  ].filter(item => item.amount > 0) : [];

  // Transform individuals data for pie chart
  const individualDistribution = dashboardData ? [
    {
      category: t("dashboard.activeIndividuals"),
      amount: dashboardData.activeIndividuals || 0,
      color: "#52c41a"
    },
    {
      category: t("dashboard.inactiveIndividuals"), 
      amount: dashboardData.inactiveIndividuals || 0,
      color: "#d9d9d9"
    },
  ].filter(item => item.amount > 0) : [];

  // Transform companies data for pie chart
  const companyStatusDistribution = dashboardData ? [
    {
      category: t("dashboard.activeCompanies"),
      amount: dashboardData.activeCompanies || 0,
      color: "#722ed1"
    },
    {
      category: t("dashboard.inactiveCompanies"),
      amount: dashboardData.inactiveCompanies || 0,
      color: "#d9d9d9"
    },
  ].filter(item => item.amount > 0) : [];

  // Transform revenue data for line/column chart
  const monthlyRevenueData = dashboardData?.monthlyRevenue 
    ? Object.entries(dashboardData.monthlyRevenue).map(([month, revenue]) => ({
        month: month,
        revenue: revenue || 0,
        formattedMonth: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      })).sort((a, b) => new Date(a.month) - new Date(b.month))
    : [];

  // Transform company employee data for table and charts
  const companiesWithEmployees = dashboardData?.employeesByCompany
    ? dashboardData.employeesByCompany
        .filter(company => company.totalEmployees > 0)
        .sort((a, b) => b.totalEmployees - a.totalEmployees)
    : [];

  // Top companies for chart
  const topCompaniesData = companiesWithEmployees
    .slice(0, 5)
    .map(company => ({
      company: company.companyName.length > 15 
        ? company.companyName.substring(0, 15) + '...' 
        : company.companyName,
      fullName: company.companyName,
      total: company.totalEmployees,
      active: company.activeEmployees,
      inactive: company.inactiveEmployees,
    }));

  // Calculate activity rate for companies
  const calculateActivityRate = (active, total) => {
    if (total === 0) return 0;
    return ((active / total) * 100);
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${BASE_URL}/Dashboard/super-admin`);
      setDashboardData(response.data);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError(err.response?.data?.message || err.message || "Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    fetchDashboardData();
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Loading state
  if (loading) {
    return (
      <div
        style={{
          padding: "24px",
          background: "#f5f5f5",
          borderRadius: "15px",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin size="large" />
        <div style={{ marginTop: "16px", textAlign: "center" }}>
          <Typography.Title level={4} style={{ color: "#8c8c8c" }}>
            {t("dashboard.loadingMessage")}
          </Typography.Title>
          <Typography.Text type="secondary">
            {t("dashboard.loadingDescription")}
          </Typography.Text>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        style={{
          padding: "24px",
          background: "#f5f5f5",
          borderRadius: "15px",
          minHeight: "100vh",
        }}
      >
        <Alert
          message={t("dashboard.errorTitle")}
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" danger onClick={handleRetry}>
              {t("dashboard.retry")}
            </Button>
          }
          style={{ marginBottom: "24px" }}
        />
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("dashboard.errorDescription")}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "24px",
        background: "#f5f5f5",
        borderRadius: "15px",
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: "24px", textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <Title level={2} style={{ margin: 0, color: "#1890ff" }}>
            {t("dashboard.title")}
          </Title>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={handleRetry}
            loading={loading}
            type="default"
          >
            {t("dashboard.refresh")}
          </Button>
        </div>
        <Text type="secondary">
          {t("dashboard.currentQuarter")}: {dashboardData?.currentQuarter || 'N/A'} | 
          {t("dashboard.totalRevenue")}: SAR {dashboardData?.totalRevenue || 0}
        </Text>
      </div>

      {/* Primary Metrics */}
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={!dashboardData}>
            <Statistic
              title={
                <Space>
                  <BankOutlined style={{ color: "#1890ff" }} />
                  <span>{t("dashboard.totalCompanies")}</span>
                </Space>
              }
              value={dashboardData?.totalCompanies || 0}
              valueStyle={{ color: "#1890ff", fontSize: "24px", fontWeight: "600" }}
              suffix={
                <div style={{ color: "#8c8c8c", fontSize: "12px", marginTop: "8px" }}>
                  <div>{t("dashboard.active")}: {dashboardData?.activeCompanies || 0}</div>
                  <div>{t("dashboard.inactive")}: {dashboardData?.inactiveCompanies || 0}</div>
                </div>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={!dashboardData}>
            <Statistic
              title={
                <Space>
                  <UsergroupAddOutlined style={{ color: "#52c41a" }} />
                  <span>{t("dashboard.totalUsers")}</span>
                </Space>
              }
              value={dashboardData?.totalUsers || 0}
              valueStyle={{ color: "#52c41a", fontSize: "24px", fontWeight: "600" }}
              suffix={
                <div style={{ color: "#8c8c8c", fontSize: "12px", marginTop: "8px" }}>
                  <div>{t("dashboard.employees")}: {dashboardData?.totalEmployees || 0}</div>
                  <div>{t("dashboard.individuals")}: {dashboardData?.totalIndividuals || 0}</div>
                </div>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={!dashboardData} className="h-full">
            <Statistic
              title={
                <Space>
                  <DollarOutlined style={{ color: "#722ed1" }} />
                  <span>{t("dashboard.totalRevenue")}</span>
                </Space>
              }
              value={dashboardData?.totalRevenue || 0}
              prefix="SAR "
              valueStyle={{ color: "#722ed1", fontSize: "24px", fontWeight: "600" }}
              suffix={
                <div style={{ color: "#8c8c8c", fontSize: "12px", marginTop: "8px" }}>
                  <div>{t("dashboard.thisYear")}: SAR {dashboardData?.yearlyRevenue?.['2025'] || 0}</div>
                </div>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={!dashboardData} className="h-full">
            <Statistic
              title={
                <Space>
                  <CheckCircleOutlined style={{ color: "#fa8c16" }} />
                  <span>{t("dashboard.activityRate")}</span>
                </Space>
              }
              value={dashboardData?.totalUsers > 0 
                ? ((dashboardData.activeUsers / dashboardData.totalUsers) * 100).toFixed(1)
                : 0
              }
              suffix="%"
              valueStyle={{ color: "#fa8c16", fontSize: "24px", fontWeight: "600" }}
              prefix={
                <div style={{ color: "#8c8c8c", fontSize: "12px", marginBottom: "8px" }}>
                  <div>{t("dashboard.q3Active")}: {dashboardData?.quarterlyActiveUsers || 0}</div>
                </div>
              }
            />
          </Card>
        </Col>
      </Row>

      {/* Secondary Metrics */}
      <Row gutter={[24, 24]} style={{ marginTop: "24px" }}>
        <Col xs={24} sm={8}>
          <Card loading={!dashboardData}>
            <Statistic
              title={
                <Space>
                  <CheckCircleOutlined style={{ color: "#52c41a" }} />
                  <span>{t("dashboard.activeUsers")}</span>
                </Space>
              }
              value={dashboardData?.activeUsers || 0}
              valueStyle={{ color: "#52c41a" }}
            />
            <Progress 
              percent={dashboardData?.totalUsers > 0 
                ? ((dashboardData.activeUsers / dashboardData.totalUsers) * 100).toFixed(2)
                : 0
              } 
              size="small"
              strokeColor="#52c41a"
              style={{ marginTop: 16 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card loading={!dashboardData}>
            <Statistic
              title={
                <Space>
                  <ClockCircleOutlined style={{ color: "#faad14" }} />
                  <span>{t("dashboard.inactiveUsers")}</span>
                </Space>
              }
              value={dashboardData?.inactiveUsers || 0}
              valueStyle={{ color: "#faad14" }}
            />
            <Progress 
              percent={dashboardData?.totalUsers > 0 
                ? ((dashboardData.inactiveUsers / dashboardData.totalUsers) * 100).toFixed(2)
                : 0
              } 
              size="small"
              strokeColor="#faad14"
              style={{ marginTop: 16 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card loading={!dashboardData} className="h-full">
            <Statistic
              title={
                <Space>
                  <UserOutlined style={{ color: "#1890ff" }} />
                  <span>{t("dashboard.individualUsers")}</span>
                </Space>
              }
              value={dashboardData?.totalIndividuals || 0}
              valueStyle={{ color: "#1890ff" }}
              suffix={
                dashboardData && (
                  <Badge
                    count={`${dashboardData?.inactiveIndividuals || 0} ${t("dashboard.inactive")}`}
                    style={{ backgroundColor: "#ff4d4f" }}
                  />
                )
              }
            />
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={[24, 24]} style={{ marginTop: "24px" }}>
        <Col xs={24} lg={8}>
          <Card 
            title={t("dashboard.userDistribution")} 
            extra={dashboardData && <Tag>{t("dashboard.total")}: {dashboardData?.totalUsers || 0}</Tag>}
            loading={!dashboardData}
          >
            {dashboardData ? (
              <AccumulationChartComponent
                id="user-donut"
                tooltip={{ enable: true, format: '${point.x}: ${point.y} (${point.percentage}%)' }}
                legendSettings={legendSettings}
                height="300px"
                background="transparent"
              >
                <Inject
                  services={[
                    AccumulationLegend,
                    PieSeries,
                    AccumulationTooltip,
                    AccumulationDataLabel,
                  ]}
                />
                <AccumulationSeriesCollectionDirective>
                  <AccumulationSeriesDirective
                    dataSource={employeeDistribution}
                    xName="category"
                    yName="amount"
                    innerRadius="50%"
                    pointColorMapping="color"
                    emptyPointSettings={{ mode: "Zero" }}
                    dataLabel={{
                      visible: true,
                      position: "Outside",
                      name: "category",
                      font: { size: "12px" }
                    }}
                  />
                </AccumulationSeriesCollectionDirective>
              </AccumulationChartComponent>
            ) : null}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card 
            title={t("dashboard.individualUsers")} 
            extra={dashboardData && <Tag>{t("dashboard.total")}: {dashboardData?.totalIndividuals || 0}</Tag>}
            loading={!dashboardData}
          >
            {dashboardData ? (
              <AccumulationChartComponent
                id="individual-donut"
                tooltip={{ enable: true, format: '${point.x}: ${point.y} (${point.percentage}%)' }}
                legendSettings={legendSettings}
                height="300px"
                background="transparent"
              >
                <Inject
                  services={[
                    AccumulationLegend,
                    PieSeries,
                    AccumulationTooltip,
                    AccumulationDataLabel,
                  ]}
                />
                <AccumulationSeriesCollectionDirective>
                  <AccumulationSeriesDirective
                    dataSource={individualDistribution}
                    xName="category"
                    yName="amount"
                    innerRadius="50%"
                    pointColorMapping="color"
                    emptyPointSettings={{ mode: "Zero" }}
                    dataLabel={{
                      visible: true,
                      position: "Outside",
                      name: "category",
                      font: { size: "12px" }
                    }}
                  />
                </AccumulationSeriesCollectionDirective>
              </AccumulationChartComponent>
            ) : null}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card 
            title={t("dashboard.companyStatus")} 
            extra={dashboardData && <Tag>{t("dashboard.total")}: {dashboardData?.totalCompanies || 0}</Tag>}
            loading={!dashboardData}
          >
            {dashboardData ? (
              <AccumulationChartComponent
                id="company-donut"
                tooltip={{ enable: true, format: '${point.x}: ${point.y} (${point.percentage}%)' }}
                legendSettings={legendSettings}
                height="300px"
                background="transparent"
              >
                <Inject
                  services={[
                    AccumulationLegend,
                    PieSeries,
                    AccumulationTooltip,
                    AccumulationDataLabel,
                  ]}
                />
                <AccumulationSeriesCollectionDirective>
                  <AccumulationSeriesDirective
                    dataSource={companyStatusDistribution}
                    xName="category"
                    yName="amount"
                    innerRadius="50%"
                    pointColorMapping="color"
                    emptyPointSettings={{ mode: "Zero" }}
                    dataLabel={{
                      visible: true,
                      position: "Outside",
                      name: "category",
                      font: { size: "12px" }
                    }}
                  />
                </AccumulationSeriesCollectionDirective>
              </AccumulationChartComponent>
            ) : null}
          </Card>
        </Col>
      </Row>

      {/* Revenue and Top Companies Section */}
      <Row gutter={[24, 24]} style={{ marginTop: "24px" }}>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <RiseOutlined style={{ color: "#1890ff" }} />
                <span>{t("dashboard.monthlyRevenueTrend")}</span>
              </Space>
            }
            loading={!dashboardData}
          >
            {dashboardData ? (
              monthlyRevenueData && monthlyRevenueData.length > 0 ? (
                <ChartComponent
                  id="revenue-chart"
                  primaryXAxis={{
                    valueType: 'Category',
                    title: t("dashboard.month"),
                    labelStyle: { size: '12px' }
                  }}
                  primaryYAxis={{
                    title: t("dashboard.revenueLabel"),
                    labelFormat: '{value}',
                    labelStyle: { size: '12px' }
                  }}
                  tooltip={{ enable: true, format: '${point.x}: SAR ${point.y}' }}
                  height="350px"
                  background="transparent"
                >
                  <Inject services={[ColumnSeries, Category, Legend, DataLabel]} />
                  <SeriesCollectionDirective>
                    <SeriesDirective
                      dataSource={monthlyRevenueData}
                      xName="formattedMonth"
                      yName="revenue"
                      name={t("dashboard.revenue")}
                      type="Column"
                      fill="#722ed1"
                      marker={{ dataLabel: { visible: true, position: 'Top' } }}
                    />
                  </SeriesCollectionDirective>
                </ChartComponent>
              ) : (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#8c8c8c' }}>
                  <RiseOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <div>{t("dashboard.noRevenueData")}</div>
                </div>
              )
            ) : null}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <BankOutlined style={{ color: "#1890ff" }} />
                <span>{t("dashboard.topCompaniesByEmployees")}</span>
              </Space>
            }
            loading={!dashboardData}
          >
            {dashboardData ? (
              topCompaniesData && topCompaniesData.length > 0 ? (
                <ChartComponent
                  id="companies-chart"
                  primaryXAxis={{
                    valueType: 'Category',
                    title: t("dashboard.companies"),
                    labelStyle: { size: '10px' },
                    labelRotation: -45
                  }}
                  primaryYAxis={{
                    title: t("dashboard.numberOfEmployees"),
                    labelStyle: { size: '12px' }
                  }}
                  tooltip={{ enable: true }}
                  height="350px"
                  background="transparent"
                >
                  <Inject services={[ColumnSeries, Category, Legend, DataLabel]} />
                  <SeriesCollectionDirective>
                    <SeriesDirective
                      dataSource={topCompaniesData}
                      xName="company"
                      yName="active"
                      name={t("dashboard.active")}
                      type="Column"
                      fill="#1890ff"
                    />
                    <SeriesDirective
                      dataSource={topCompaniesData}
                      xName="company"
                      yName="inactive"
                      name={t("dashboard.inactive")}
                      type="Column"
                      fill="#d9d9d9"
                    />
                  </SeriesCollectionDirective>
                </ChartComponent>
              ) : (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#8c8c8c' }}>
                  <BankOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <div>{t("dashboard.noCompanyData")}</div>
                </div>
              )
            ) : null}
          </Card>
        </Col>
      </Row>

      {/* Company Breakdown */}
      <Row style={{ marginTop: "24px" }}>
        <Col xs={24}>
          <Card 
            title={
              <Space>
                <BankOutlined style={{ color: "#1890ff" }} />
                <span>{t("dashboard.companiesOverview")}</span>
                {dashboardData && <Badge count={dashboardData?.totalCompanies || 0} style={{ backgroundColor: "#1890ff" }} />}
              </Space>
            }
            extra={
              dashboardData && (
                <Space>
                  <Tag>{t("dashboard.active")}: {dashboardData?.activeCompanies || 0}</Tag>
                  <Tag>{t("dashboard.inactive")}: {dashboardData?.inactiveCompanies || 0}</Tag>
                </Space>
              )
            }
          >
            <Table
              dataSource={dashboardData?.employeesByCompany || []}
              loading={!dashboardData}
              scroll={{ x: 1000 }}
              pagination={{ 
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} companies`
              }}
              size="middle"
              rowKey="companyId"
              columns={[
                {
                  title: t("dashboard.companyName"),
                  dataIndex: "companyName",
                  key: "companyName",
                  width: 250,
                  render: (text) => (
                    <div>
                      <Text strong style={{ fontSize: "14px" }}>{text}</Text>
                    </div>
                  ),
                  sorter: (a, b) => a.companyName.localeCompare(b.companyName),
                  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                    <div style={{ padding: 8 }}>
                      <input
                        placeholder={t("dashboard.searchCompanyName")}
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                      />
                    </div>
                  ),
                  onFilter: (value, record) =>
                    record.companyName.toLowerCase().includes(value.toLowerCase()),
                },
                {
                  title: t("dashboard.totalEmployees"),
                  dataIndex: "totalEmployees",
                  key: "totalEmployees",
                  width: 150,
                  render: (count) => (
                    <Space>
                      <UsergroupAddOutlined style={{ color: "#1890ff" }} />
                      <Text strong>{count}</Text>
                    </Space>
                  ),
                  sorter: (a, b) => a.totalEmployees - b.totalEmployees,
                  defaultSortOrder: 'descend',
                },
                {
                  title: t("dashboard.active"),
                  dataIndex: "activeEmployees",
                  key: "activeEmployees",
                  width: 120,
                  render: (count) => (
                    <Space>
                      <CheckCircleOutlined style={{ color: "#52c41a" }} />
                      <Text style={{ color: "#52c41a", fontWeight: "bold" }}>{count}</Text>
                    </Space>
                  ),
                  sorter: (a, b) => a.activeEmployees - b.activeEmployees,
                },
                {
                  title: t("dashboard.inactive"),
                  dataIndex: "inactiveEmployees",
                  key: "inactiveEmployees",
                  width: 120,
                  render: (count) => (
                    <Space>
                      <ClockCircleOutlined style={{ color: "#faad14" }} />
                      <Text style={{ color: "#faad14", fontWeight: "bold" }}>{count}</Text>
                    </Space>
                  ),
                  sorter: (a, b) => a.inactiveEmployees - b.inactiveEmployees,
                },
                {
                  title: t("dashboard.activityRate"),
                  key: "activityRate",
                  width: 150,
                  render: (_, record) => {
                    if (record.totalEmployees === 0) {
                      return (
                        <div style={{ textAlign: 'center' }}>
                          <Text type="secondary">{t("dashboard.noEmployees")}</Text>
                        </div>
                      );
                    }
                    const rate = calculateActivityRate(record.activeEmployees, record.totalEmployees);
                    let color = "#f5f5f5"; // Light gray for low
                    let status = "exception";
                    
                    if (rate >= 80) {
                      color = "#1890ff"; // Blue for high
                      status = "success";
                    } else if (rate >= 50) {
                      color = "#52c41a"; // Green for medium
                      status = "active";
                    } else if (rate > 0) {
                      color = "#faad14"; // Orange for low but not zero
                    }

                    return (
                      <div style={{ textAlign: 'center' }}>
                        <Progress
                          type="circle"
                          percent={rate}
                          size={50}
                          strokeColor={color}
                          format={(percent) => `${percent?.toFixed(0)}%`}
                        />
                      </div>
                    );
                  },
                  sorter: (a, b) => {
                    const aRate = calculateActivityRate(a.activeEmployees, a.totalEmployees);
                    const bRate = calculateActivityRate(b.activeEmployees, b.totalEmployees);
                    return aRate - bRate;
                  },
                },
                {
                  title: t("dashboard.status"),
                  key: "status",
                  width: 120,
                  render: (_, record) => {
                    const rate = calculateActivityRate(record.activeEmployees, record.totalEmployees);
                    
                    if (record.totalEmployees === 0) {
                      return <Tag>{t("dashboard.noEmployees")}</Tag>;
                    } else if (rate >= 80) {
                      return <Tag color="blue">{t("dashboard.excellent")}</Tag>;
                    } else if (rate >= 50) {
                      return <Tag color="green">{t("dashboard.good")}</Tag>;
                    } else if (rate > 0) {
                      return <Tag color="orange">{t("dashboard.needsAttention")}</Tag>;
                    } else {
                      return <Tag color="default">{t("dashboard.inactive")}</Tag>;
                    }
                  },
                  filters: [
                    { text: t("dashboard.excellent80"), value: 'excellent' },
                    { text: t("dashboard.good5079"), value: 'good' },
                    { text: t("dashboard.needsAttention50"), value: 'attention' },
                    { text: t("dashboard.noEmployees"), value: 'none' },
                  ],
                  onFilter: (value, record) => {
                    const rate = calculateActivityRate(record.activeEmployees, record.totalEmployees);
                    if (value === 'excellent') return rate >= 80;
                    if (value === 'good') return rate >= 50 && rate < 80;
                    if (value === 'attention') return rate > 0 && rate < 50;
                    if (value === 'none') return record.totalEmployees === 0;
                    return true;
                  },
                },
              ]}
              summary={(pageData) => {
                let totalEmployees = 0;
                let totalActive = 0;
                let totalInactive = 0;

                pageData.forEach(({ totalEmployees: emp, activeEmployees: active, inactiveEmployees: inactive }) => {
                  totalEmployees += emp;
                  totalActive += active;
                  totalInactive += inactive;
                });

                const overallRate = totalEmployees > 0 ? ((totalActive / totalEmployees) * 100).toFixed(1) : '0';

                return (
                  <Table.Summary.Row style={{ backgroundColor: '#f5f5f5' }}>
                    <Table.Summary.Cell>
                      <Text strong>{t("dashboard.pageTotal", { count: pageData.length })}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell>
                      <Text strong>{totalEmployees}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell>
                      <Text strong style={{ color: '#52c41a' }}>{totalActive}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell>
                      <Text strong style={{ color: '#faad14' }}>{totalInactive}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell>
                      <Text strong>{overallRate}%</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell>
                      <Tag color={overallRate >= 70 ? 'blue' : overallRate >= 50 ? 'green' : 'orange'}>
                        {overallRate >= 70 ? t("dashboard.good") : overallRate >= 50 ? t("dashboard.average") : t("dashboard.poor")}
                      </Tag>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SuperAdminDashboard;
